// payoutShared.js
// Shared, presentation-only helpers for the team Payout report (styles, number
// formatting, canonical field list, PDF logo, payout-rule label). Kept separate
// so the team page and any future payout view render identically.

export const PAYOUT_RULE_LABELS = {
  1: "Fixed amount per month or % per unit (whichever is higher)",
  2: "Fixed amount per month or fixed per unit (whichever is higher)",
  3: "Fixed amount per month + % per unit",
  4: "Fixed amount per month + fixed per unit",
  5: "% per unit",
  6: "Fixed per unit",
  7: "Fixed amount per month",
};

// The per-charger monthly fee, spelled out with its own GST rate — e.g.
// "AC 199 / DC 349 per charger per month + 18% GST". Falls back to the annual
// figure divided by twelve, exactly as the payout engine does. Returns "" when no
// fixed fee is configured, or when the report predates these meta fields (a
// finalized month, whose snapshot never stored them).
const monthlyFixedLabel = (meta) => {
  const monthly = (m, a) => {
    const mv = Number(m) || 0;
    return mv > 0 ? mv : (Number(a) || 0) / 12;
  };
  const ac = monthly(meta.acChargesMonthly, meta.acChargesAnnual);
  const dc = monthly(meta.dcChargesMonthly, meta.dcChargesAnnual);
  if (ac <= 0 && dc <= 0) return "";
  const gst = Number(meta.chargerTaxPercent) || 0;
  const parts = [];
  if (ac > 0) parts.push(`AC ${Number(ac.toFixed(2))}`);
  if (dc > 0) parts.push(`DC ${Number(dc.toFixed(2))}`);
  return `${parts.join(" / ")} per charger per month${gst > 0 ? ` + ${gst}% GST` : ""}`;
};

// Human-readable payout rule with the exact configured rates, e.g.
// "Fixed amount per month + fixed per unit — AC 199 / DC 349 per charger per
// month + 18% GST, plus 1/kWh + 18% GST".
//
// Both legs are named. Rules 1-4 and 7 all charge a per-charger monthly fee, and
// for a lightly used charger it is the whole fee — naming only the variable rate
// made the Amik CMS Fee look like a pure per-kWh or percentage charge and left
// the fixed amount unaccounted for on the report.
export const formatPayoutRule = (meta) => {
  if (!meta) return "-";
  const base =
    PAYOUT_RULE_LABELS[meta.payoutRule] || String(meta.payoutRule || "-");
  const rule = meta.payoutRule;
  const pct = Number(meta.commissionPercent);
  const cost = Number(meta.commissionCost);
  const gst = Number(meta.taxPercent);
  const gstSuffix = gst > 0 ? ` + ${gst}% GST` : "";
  let variable = "";
  if ([1, 3, 5].includes(rule) && pct > 0) variable = `${pct}%${gstSuffix}`;
  else if ([2, 4, 6].includes(rule) && cost > 0) variable = `${cost}/kWh${gstSuffix}`;
  const fixed = [1, 2, 3, 4, 7].includes(rule) ? monthlyFixedLabel(meta) : "";

  if (fixed && variable) {
    // 1 and 2 take whichever leg is larger; 3 and 4 charge both.
    return [1, 2].includes(rule)
      ? `${base} — higher of ${fixed}, or ${variable}`
      : `${base} — ${fixed}, plus ${variable}`;
  }
  if (fixed) return `${base} — ${fixed}`;
  if (variable) return `${base} — ${variable}`;
  return base;
};

// Excel (xlsx-js-style) style tokens.
export const NAVY = "1F3864";
export const YELLOW = "FFF2A8";
export const LIGHT = "EAF0F8";
const THIN = {
  top: { style: "thin", color: { rgb: "D0D7E2" } },
  bottom: { style: "thin", color: { rgb: "D0D7E2" } },
  left: { style: "thin", color: { rgb: "D0D7E2" } },
  right: { style: "thin", color: { rgb: "D0D7E2" } },
};
export const S = {
  title: { font: { bold: true, sz: 16, color: { rgb: NAVY } } },
  subtitle: { font: { bold: true, sz: 12, color: { rgb: "333333" } } },
  meta: { font: { sz: 10, color: { rgb: "666666" } } },
  section: {
    font: { bold: true, sz: 11, color: { rgb: NAVY } },
    fill: { fgColor: { rgb: LIGHT } },
  },
  header: {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: NAVY } },
    alignment: { horizontal: "center", vertical: "center" },
    border: THIN,
  },
  label: { font: { bold: false }, border: THIN },
  labelBold: { font: { bold: true }, border: THIN },
  money: { numFmt: "#,##0.00", alignment: { horizontal: "right" }, border: THIN },
  moneyBold: {
    numFmt: "#,##0.00",
    font: { bold: true },
    alignment: { horizontal: "right" },
    border: THIN,
  },
  number: { numFmt: "#,##0", alignment: { horizontal: "right" }, border: THIN },
  highlight: {
    numFmt: "#,##0.00",
    font: { bold: true },
    fill: { fgColor: { rgb: YELLOW } },
    alignment: { horizontal: "right" },
    border: THIN,
  },
  note: { font: { italic: true, sz: 9, color: { rgb: "666666" } } },
};

export const sumOverDelivered = (txns) =>
  (txns || []).reduce((a, t) => a + (t.overDeliveredAmount || 0), 0);

// Payout summary rows (Without GST / With GST), shared by the team page and the
// charger tab so both bridge identically. When collection is below the gross
// Charging Amount Collected (payment-gateway charges and/or over-delivery
// adjustments), the top rows bridge it down so the summaries reconcile.
export const buildPayoutSummaryRows = (
  summary,
  payout,
  meta,
  brand,
  overDeliveredTotal = 0,
  byPaymentType = []
) => {
  // Fee-rate suffix mirrors the payout-rule label shown below the summary (see
  // formatPayoutRule): percentage only for %-based rules, per-kWh for fixed-per-unit
  // rules, and no rate for fixed-monthly — so the fee label never shows a stray %.
  const pct = Number(meta?.commissionPercent) || 0;
  const cost = Number(meta?.commissionCost) || 0;
  const rule = meta?.payoutRule;
  let rateSuffix = "";
  if ([1, 3, 5].includes(rule) && pct > 0) rateSuffix = `${pct}% `;
  else if ([2, 4, 6].includes(rule) && cost > 0) rateSuffix = `${cost}/kWh `;
  // Name the monthly fixed leg too. Rules 3 and 4 add it to the variable rate and
  // rules 1 and 2 floor the fee at it, so a row reading only "1/kWh" understated
  // what the fee is made of — most visibly on an idle charger, where the entire
  // fee is the monthly amount and the per-kWh leg is zero.
  const hasFixed =
    [1, 2, 3, 4, 7].includes(rule) &&
    (Number(meta?.acChargesMonthly) > 0 ||
      Number(meta?.dcChargesMonthly) > 0 ||
      Number(meta?.acChargesAnnual) > 0 ||
      Number(meta?.dcChargesAnnual) > 0);
  if (hasFixed) {
    rateSuffix = rateSuffix
      ? [1, 2].includes(rule)
        ? `monthly fixed or ${rateSuffix}`
        : `monthly fixed + ${rateSuffix}`
      : "monthly fixed ";
  }
  const feeLabel = `${brand} CMS Fee (${rateSuffix}incl. GST)`;
  const gap =
    Number(summary.totalChargingCost) - Number(payout.totalCollectionByAmik);
  const od = Number(overDeliveredTotal) || 0;
  // RFID & OCPI are settled outside Amik and excluded from the payout collection
  // (the Amik CMS Fee still applies to them). Shown as separate bridge lines,
  // each sourced from byPaymentType so the with/without-GST amounts are exact.
  const excl = Number(payout?.excludedCharging) || 0;
  const exclWoGst = Number(payout?.excludedConsumption) || 0;
  const pg = gap - od - excl;
  // One "Less:" line per settled-outside type, in a stable order.
  const exclLine = (type, label) => {
    const p = (byPaymentType || []).find((x) => x.paymentType === type);
    if (!p) return null;
    const wGst = Number(p.totalChargingCost) || 0;
    if (wGst <= 0.005) return null;
    const woGst = wGst - (Number(p.totalTaxAmount) || 0);
    return { label, woGst: -woGst, wGst: -wGst };
  };
  const settledOutsideRows = [
    exclLine("RFID", "Less: RFID (settled outside Amik)"),
    exclLine("OCPI", "Less: OCPI (settled outside Amik)"),
  ].filter(Boolean);
  const rows = [];
  if (gap > 0.005) {
    rows.push({
      label: "Total Charging Amount Collected",
      woGst: summary.totalConsumptionCost,
      wGst: summary.totalChargingCost,
    });
    if (settledOutsideRows.length) {
      settledOutsideRows.forEach((r) => rows.push(r));
    } else if (excl > 0.005) {
      // Fallback for pre-split snapshots without per-type byPaymentType data.
      rows.push({
        label: "Less: RFID & OCPI (settled outside Amik)",
        woGst: -exclWoGst,
        wGst: -excl,
      });
    }
    if (pg > 0.005) rows.push({ label: "Less: PG Charges", woGst: -pg, wGst: -pg });
    if (od > 0.005)
      rows.push({
        label: "Less: Over-delivery Adjustment",
        woGst: -od,
        wGst: -od,
      });
  }
  rows.push({
    label: "Total Collection By Amik",
    woGst: payout.baseAmount,
    wGst: payout.totalCollectionByAmik,
  });
  rows.push({ label: feeLabel, woGst: payout.amikFee, wGst: payout.amikFee });
  rows.push({
    label: "Payout",
    woGst: payout.payoutExclGst,
    wGst: payout.payout,
    highlight: true,
  });
  return rows;
};

// DB timestamps are stored in UTC (returned as naive strings). Render them in IST
// with an explicit timezone label, independent of the viewer's browser zone, so a
// late-evening-UTC session (e.g. 2026-06-30 20:01 UTC = 2026-07-01 01:31 IST) reads
// as the IST date it is actually attributed to.
export const fmtTime = (v) => {
  if (!v) return "";
  // Normalise to a naive datetime then force-parse as UTC: drop any fractional
  // seconds and any trailing zone (Z or ±HH:MM) the backend might include.
  const iso =
    String(v)
      .trim()
      .replace(" ", "T")
      .replace(/\.\d+/, "")
      .replace(/(?:Z|[+-]\d{2}:?\d{2})$/, "") + "Z";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(v);
  const p = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .formatToParts(d)
    .reduce((a, x) => ((a[x.type] = x.value), a), {});
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second} IST`;
};

export const money = (n) => (Number(n) || 0).toFixed(2);
export const withComma = (n) =>
  (Number(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Canonical Charging Summary field list. Price per kWh is optional: for a team
// (many chargers with possibly different tariffs) a blended average is misleading,
// so it is shown per charger instead and omitted here with { includePrice: false }.
// The three "collected by payment type" sections, shared by the team page and the
// charger tab and by their on-screen / Excel / PDF renderers so every surface shows
// the same breakdown: collected types (UPI/CARD/…), RFID, and OCPI segregated by
// roaming company. Each section carries its own rows, caption and column label.
// OCPI-by-company is aggregated from the transactions (each OCPI txn carries its
// ocpiCompany); its rows sum to the OCPI byPaymentType bucket.
export const buildPaymentTypeSections = (byPaymentType = [], transactions = []) => {
  const bpt = byPaymentType || [];
  const isSettledOutside = (t) => t === "RFID" || t === "OCPI";
  const norm = (p) => ({
    paymentType: p.paymentType,
    transactions: Number(p.transactions) || 0,
    totalChargingCost: Number(p.totalChargingCost) || 0,
  });
  const collected = bpt.filter((p) => !isSettledOutside(p.paymentType)).map(norm);
  const rfid = bpt.filter((p) => p.paymentType === "RFID").map(norm);
  const ocpiByCompany = Object.values(
    (transactions || [])
      .filter((t) => t.paymentType === "OCPI")
      .reduce((acc, t) => {
        const key = t.ocpiCompany || "Unknown";
        if (!acc[key]) {
          acc[key] = { paymentType: key, transactions: 0, totalChargingCost: 0 };
        }
        acc[key].transactions += 1;
        acc[key].totalChargingCost += Number(t.chargingCost) || 0;
        return acc;
      }, {})
  ).sort((a, b) => b.totalChargingCost - a.totalChargingCost);
  return [
    {
      key: "collected",
      title: "Charging Amount Collected by Payment Type",
      firstColLabel: "Payment Type",
      caption: "Collected by Amik (UPI, card, etc.).",
      emptyText: "No collected transactions.",
      rows: collected,
    },
    {
      key: "rfid",
      title: "RFID (settled outside Amik)",
      firstColLabel: "Payment Type",
      caption: "Not collected by Amik; the Amik CMS fee still applies.",
      emptyText: "No RFID transactions.",
      rows: rfid,
    },
    {
      key: "ocpi",
      title: "OCPI by Company (settled outside Amik)",
      firstColLabel: "OCPI Company",
      caption: "Roaming — settled by each partner; the Amik CMS fee still applies.",
      emptyText: "No OCPI transactions.",
      rows: ocpiByCompany,
    },
  ];
};

export const buildChargingSummary = (summary, opts = {}) => {
  const rows = [
    { label: "Total Transactions", value: summary.totalTransactions, int: true },
    { label: "Total kWh Consumed", value: summary.totalKWh },
  ];
  // Optional kWh breakdown by settlement bucket: payment collected (UPI/CARD/…)
  // vs OCPI vs RFID. Only shown when some energy was settled outside Amik, so a
  // pure pay-as-you-go team is not cluttered with a redundant single sub-row.
  const byPT = opts.byPaymentType || [];
  if (byPT.length) {
    const kwhOf = (type) => {
      const p = byPT.find((x) => x.paymentType === type);
      return p ? Number(p.totalKWh) || 0 : 0;
    };
    const rfidKWh = kwhOf("RFID");
    const ocpiKWh = kwhOf("OCPI");
    const collectedKWh = (Number(summary.totalKWh) || 0) - rfidKWh - ocpiKWh;
    if (rfidKWh > 0.0005 || ocpiKWh > 0.0005) {
      rows.push({
        label: "   • Payment Collected (kWh)",
        value: collectedKWh,
        sub: true,
      });
      if (ocpiKWh > 0.0005)
        rows.push({ label: "   • OCPI (kWh)", value: ocpiKWh, sub: true });
      if (rfidKWh > 0.0005)
        rows.push({ label: "   • RFID (kWh)", value: rfidKWh, sub: true });
    }
  }
  if (opts.includePrice !== false) {
    rows.push(
      { label: "Avg Price per kWh (excl. GST)", value: summary.avgPricePerKwExclGst },
      { label: "+ GST per kWh", value: summary.avgGstPerKw },
      {
        label: "= Avg Price per kWh (incl. GST)",
        value: summary.avgPricePerKw,
        bold: true,
      }
    );
  }
  rows.push(
    { label: "Total Consumption Cost", value: summary.totalConsumptionCost },
    { label: "Total GST", value: summary.totalTaxAmount },
    { label: "Total Charging Amount Collected", value: summary.totalChargingCost }
  );
  // Split the money the same way the kWh above is split. The three totals are
  // gross across every payment type, but RFID and OCPI are settled outside Amik
  // and never reach the payout — so a team whose energy is mostly RFID reads as
  // having collected hundreds when Amik actually collected tens. The Payout
  // Summary already bridges this down with its "Less:" lines; showing the same
  // split here means the two cards reconcile without cross-referencing, and the
  // "Payment Collected" figure matches the collection column in Transaction
  // Details. Only shown when some energy really was settled outside Amik.
  if (byPT.length) {
    const costOf = (type) => {
      const p = byPT.find((x) => x.paymentType === type);
      return p ? Number(p.totalChargingCost) || 0 : 0;
    };
    const rfidCost = costOf("RFID");
    const ocpiCost = costOf("OCPI");
    if (rfidCost > 0.005 || ocpiCost > 0.005) {
      const gross = Number(summary.totalChargingCost) || 0;
      // Take the collected figure from the payout block itself rather than deriving
      // it, so this row is identical to the paisa to "Total Collection By Amik" in
      // the Payout Summary. The two cards sit side by side and any difference reads
      // as an error — deriving it left a gap equal to the payment-gateway charge.
      // What is left over (PG charges, and any over-delivery adjustment) gets its
      // own line, so the parts still add back to the gross above.
      const collected = opts.payout
        ? Number(opts.payout.totalCollectionByAmik) || 0
        : Number((gross - rfidCost - ocpiCost).toFixed(2));
      const residual = Number((gross - collected - rfidCost - ocpiCost).toFixed(2));
      rows.push({
        label: "   • Total Collection By Amik",
        value: collected,
        sub: true,
      });
      if (Math.abs(residual) > 0.005)
        rows.push({
          label: "   • PG charges & adjustments",
          value: residual,
          sub: true,
        });
      if (ocpiCost > 0.005)
        rows.push({
          label: "   • OCPI (settled outside Amik)",
          value: ocpiCost,
          sub: true,
        });
      if (rfidCost > 0.005)
        rows.push({
          label: "   • RFID (settled outside Amik)",
          value: rfidCost,
          sub: true,
        });
    }
  }
  return rows;
};

// Loads the Amik logo as a PNG data URL for the PDF; resolves null on failure.
export const loadLogoDataUrl = () =>
  new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const w = img.naturalWidth || 240;
          const h = img.naturalHeight || 90;
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          resolve({ dataUrl: canvas.toDataURL("image/png"), width: w, height: h });
        } catch (e) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = "/logo/amikLogo.svg";
    } catch (e) {
      resolve(null);
    }
  });
